// components/servicing_dashboard/sections/TimeMetrics.jsx

import React from 'react';
import { 
  ClockIcon, 
  BuildingOfficeIcon,
  ChartBarIcon,
  CalendarIcon
} from '@heroicons/react/24/outline';

import MetricCard from '../../common/MetricCard';
import { formatHours, formatCurrency, formatPercentage, formatNumber } from '../../../utils';

const TimeMetrics = ({ summaryStats, onCardClick, dateRange }) => {
  if (!summaryStats) {
    return (
      <div className="text-center py-8 text-gray-500">
        <p>Loading metrics...</p>
      </div>
    );
  }
  
  const totalHours = parseFloat(summaryStats.total_hours) || 0;
  const billableHours = parseFloat(summaryStats.total_billable_hours) || 0;
  const timeOffHours = parseFloat(summaryStats.time_off_hours) || 0; // Vacation, Sick, Holiday
  const availableHours = totalHours - timeOffHours;
  const nonClientHours = totalHours - billableHours;
  
  // Calculate productivity based on available hours
  const utilizationRate = availableHours > 0 ? billableHours / availableHours : 0;

  // Determine productivity color based on utilization rate
  const getProductivityColor = (rate) => {
    if (rate >= 0.75) return 'green';
    if (rate >= 0.5) return 'yellow';
    return 'red';
  };

  return (
    <div className="space-y-6">
      {/* Metrics Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <MetricCard
          title="Total Hours"
          value={formatHours(totalHours)}
          icon={ClockIcon}
          loading={false}
          isClickable={false}
          changeLabel=''
          onClick={() => onCardClick?.('total_hours')}
        />

        <MetricCard
          title="Available Hours"
          value={formatHours(availableHours)}
          icon={CalendarIcon}
          loading={false}
          isClickable={false}
          changeLabel='Excludes PTO'
          onClick={() => onCardClick?.('available_hours')}
        />

        <MetricCard
          title="Client Hours"
          value={formatHours(billableHours)}
          icon={BuildingOfficeIcon}
          loading={false}
          isClickable={false}
          changeLabel=''
          onClick={() => onCardClick?.('client_hours')}
        />

        <MetricCard
          title="Non-Client Hours"
          value={formatHours(nonClientHours)}
          icon={ChartBarIcon}
          loading={false}
          isClickable={false}
          changeLabel=''
          onClick={() => onCardClick?.('non_client_hours')}
        />
      </div>

      {/* Utilization Benchmark */}
      <div className="bg-white rounded-lg shadow-sm border p-6">
        <div className="flex items-center justify-between mb-4">
          <h4 className="text-lg font-medium text-gray-900">Productivity Benchmark</h4>
          <div className="text-sm text-gray-600 font-medium">Target: 75%</div>
        </div>
        
        <div className="w-full bg-gray-200 rounded-full h-14 overflow-hidden">
          <div 
            className={`h-full rounded-full transition-all duration-500 flex items-center justify-end pr-3 ${
              utilizationRate >= 0.75 ? 'bg-violet-500' :
              utilizationRate >= 0.5 ? 'bg-violet-500' : 'bg-pink-500'
            }`}
            style={{ width: `${Math.min(utilizationRate * 100, 100)}%` }}
          >
            <span className="text-white text-sm font-semibold">
              {formatPercentage(utilizationRate)}
            </span>
          </div>
        </div>
        
        <div className="flex justify-between mt-3 text-sm text-gray-600">
          <span>0%</span>
          <span>50%</span>
          <span className="font-medium">75%</span>
          <span>100%</span>
        </div>
      </div>
    </div>
  );
};

export default TimeMetrics;